import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Warning, ArrowsClockwise, CheckCircle, Envelope, Phone } from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * DeliveryIssueWarning - Inline warning for failed email/sms delivery
 * 
 * Shows when customer has a bad_recipient failure (invalid email/phone).
 * After contact is fixed, shows "Resend" button.
 * 
 * Props:
 * - customerId: Customer ID to check for issues
 * - channel: "email" or "sms" - which channel to show warning for
 * - currentValue: Current email/phone value for this channel
 * - onFixed: Callback when contact has been updated (to refresh issues)
 */
export function DeliveryIssueWarning({ 
  customerId, 
  channel, 
  currentValue,
  onFixed,
  className = "" 
}) {
  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    if (customerId) {
      fetchIssues();
    }
  }, [customerId, currentValue]);

  const fetchIssues = async () => {
    try {
      const res = await fetch(
        `${API}/comms/customers/${customerId}/delivery-issues`,
        { credentials: "include" }
      );
      if (res.ok) {
        const data = await res.json();
        const issueData = channel === "email" 
          ? data.last_failed_email 
          : data.last_failed_sms;
        setIssue(issueData);
        setResent(false); // Reset resent state when checking new issues
      }
    } catch (err) {
      console.error("Error fetching delivery issues:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!issue?.ledger_id) return;
    
    setResending(true);
    try {
      const res = await fetch(`${API}/comms/resend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ledger_id: issue.ledger_id })
      });
      
      const data = await res.json();
      
      if (res.ok && data.ok) {
        toast.success(`Message resent successfully to ${data.recipient}`);
        setResent(true);
        setIssue(null);
        if (onFixed) onFixed();
      } else {
        toast.error(data.detail || data.error_message || "Failed to resend");
      }
    } catch (err) {
      toast.error("Failed to resend message");
    } finally {
      setResending(false);
    }
  };

  // Check if contact has been updated since the failure
  const contactWasUpdated = () => {
    if (!issue) return false;
    const failedRecipient = issue.recipient?.toLowerCase() || "";
    const current = currentValue?.toLowerCase() || "";
    // Different email/phone means it was updated
    return current && current !== failedRecipient;
  };

  if (loading || !issue) {
    if (resent) {
      return (
        <div className={`flex items-center gap-2 text-emerald-600 text-sm ${className}`}>
          <CheckCircle size={16} weight="fill" />
          <span>Message resent successfully</span>
        </div>
      );
    }
    return null;
  }

  const Icon = channel === "email" ? Envelope : Phone;
  const contactType = channel === "email" ? "email" : "phone number";
  const isFixed = contactWasUpdated();

  // Format error message for display
  const getErrorDisplay = () => {
    const code = issue.error_code || "";
    const msg = issue.error_message || "";
    
    const errorMap = {
      "INVALID_EMAIL": "Invalid email address",
      "INVALID_EMAIL_ADDRESS": "Invalid email address format",
      "HARD_BOUNCE": "Email bounced - address does not exist",
      "BOUNCE": "Email bounced - delivery failed",
      "INVALID_PHONE": "Invalid phone number",
      "INVALID_TO_NUMBER": "Invalid phone number",
      "LANDLINE": "Cannot send SMS to landline",
      "NOT_SMS_CAPABLE": "Phone number cannot receive SMS",
      "MISSING_RECIPIENT": "No contact info provided",
      "UNDELIVERABLE": "Message undeliverable",
    };
    
    return errorMap[code] || msg || "Delivery failed";
  };

  // Format what notification failed
  const getNotificationDisplay = () => {
    const kind = issue.notification_kind || "";
    const entity = issue.entity_type || "";
    
    if (kind.includes("invoice")) return `Invoice ${channel}`;
    if (kind.includes("estimate")) return `Estimate ${channel}`;
    if (kind.includes("review")) return `Review request`;
    if (kind.includes("reminder")) return `Appointment reminder`;
    if (entity === "invoice") return `Invoice ${channel}`;
    if (entity === "estimate") return `Estimate ${channel}`;
    
    return `${channel === "email" ? "Email" : "SMS"} notification`;
  };

  return (
    <div className={`p-3 rounded-sm border-2 ${isFixed ? "bg-amber-50 border-amber-300" : "bg-red-50 border-red-300"} ${className}`}>
      <div className="flex items-start gap-3">
        <Warning 
          size={20} 
          weight="fill" 
          className={isFixed ? "text-amber-500 mt-0.5" : "text-red-500 mt-0.5"} 
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-medium text-sm ${isFixed ? "text-amber-700" : "text-red-700"}`}>
              {getNotificationDisplay()} failed
            </span>
            <span className="text-xs text-zinc-500">
              {getErrorDisplay()}
            </span>
          </div>
          
          {!isFixed ? (
            <p className="text-sm text-zinc-600 mt-1">
              Update the customer's {contactType} to resend the message.
            </p>
          ) : (
            <div className="flex items-center gap-2 mt-2">
              <Button
                size="sm"
                onClick={handleResend}
                disabled={resending}
                className="bg-[#FF6600] hover:bg-[#CC5200] text-white h-8 text-xs gap-1"
                data-testid={`resend-${channel}-btn`}
              >
                <ArrowsClockwise size={14} className={resending ? "animate-spin" : ""} />
                {resending ? "Resending..." : "Resend Message"}
              </Button>
              <span className="text-xs text-zinc-500">
                {contactType} updated
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * EntityDeliveryBanner - Banner for failed notifications on entity pages (Invoice, Estimate)
 * 
 * Shows at top of invoice/estimate detail when there's a bad_recipient failure.
 */
export function EntityDeliveryBanner({
  entityType,
  entityId,
  customerId,
  onNavigateToCustomer,
  className = ""
}) {
  const [issues, setIssues] = useState({ email: null, sms: null });
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(null);

  useEffect(() => {
    fetchIssues();
  }, [entityType, entityId]);

  const fetchIssues = async () => {
    try {
      const res = await fetch(
        `${API}/comms/entity/${entityType}/${entityId}/delivery-issues`,
        { credentials: "include" }
      );
      if (res.ok) {
        const data = await res.json();
        setIssues({
          email: data.last_failed_email,
          sms: data.last_failed_sms
        });
      }
    } catch (err) {
      console.error("Error fetching entity delivery issues:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (channel, ledgerId) => {
    setResending(channel);
    try {
      const res = await fetch(`${API}/comms/resend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ledger_id: ledgerId })
      });
      
      const data = await res.json();
      
      if (res.ok && data.ok) {
        toast.success(`${channel === "email" ? "Email" : "SMS"} resent successfully`);
        fetchIssues(); // Refresh
      } else {
        if (data.detail?.includes("still invalid")) {
          toast.error("Please update customer contact info first");
        } else {
          toast.error(data.detail || data.error_message || "Failed to resend");
        }
      }
    } catch (err) {
      toast.error("Failed to resend");
    } finally {
      setResending(null);
    }
  };

  if (loading || (!issues.email && !issues.sms)) {
    return null;
  }

  const getEntityName = () => {
    if (entityType === "invoice") return "Invoice";
    if (entityType === "estimate") return "Estimate";
    return entityType;
  };

  return (
    <div className={`bg-red-50 border-2 border-red-300 rounded-sm p-4 ${className}`}>
      <div className="flex items-start gap-3">
        <Warning size={24} weight="fill" className="text-red-500 flex-shrink-0" />
        <div className="flex-1">
          <h4 className="font-medium text-red-700">
            {getEntityName()} notification failed
          </h4>
          
          {issues.email && (
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-sm text-zinc-600">
                <Envelope size={16} />
                <span>Email to <span className="font-mono text-xs">{issues.email.recipient}</span> failed</span>
                <span className="text-xs text-zinc-400">({issues.email.error_code})</span>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleResend("email", issues.email.ledger_id)}
                disabled={resending === "email"}
                className="h-7 text-xs gap-1"
                data-testid="resend-entity-email-btn"
              >
                <ArrowsClockwise size={12} className={resending === "email" ? "animate-spin" : ""} />
                Resend
              </Button>
            </div>
          )}
          
          {issues.sms && (
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-sm text-zinc-600">
                <Phone size={16} />
                <span>SMS to <span className="font-mono text-xs">{issues.sms.recipient}</span> failed</span>
                <span className="text-xs text-zinc-400">({issues.sms.error_code})</span>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleResend("sms", issues.sms.ledger_id)}
                disabled={resending === "sms"}
                className="h-7 text-xs gap-1"
                data-testid="resend-entity-sms-btn"
              >
                <ArrowsClockwise size={12} className={resending === "sms" ? "animate-spin" : ""} />
                Resend
              </Button>
            </div>
          )}
          
          <div className="mt-3 flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={onNavigateToCustomer}
              className="h-7 text-xs border-red-300 text-red-700 hover:bg-red-100"
              data-testid="fix-customer-contact-btn"
            >
              Fix Customer Contact Info
            </Button>
            <span className="text-xs text-zinc-500">
              Update contact info then resend
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DeliveryIssueWarning;
